'use client';

import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { ProductFiltersButton } from '@/features/products/components/list/filters/productFiltersButton';
import { ProductFiltersItem } from '@/features/products/components/list/filters/productFiltersItem';
import { ProductSortingButton } from '@/features/products/components/list/filters/productSortingButton';
import { ResetFiltersButton } from '@/features/products/components/list/filters/resetFiltersButton';
import { getFormattedFilteredParams } from '@/features/products/components/list/filters/utils';
import {
  useProductListFilters,
  useProductListSelectedFilters,
  useProductListSelectedFiltersCount,
  useProductListTaxonDefaultSortingOption,
  useProductListTaxonSlug,
  useProductListTotalItems
} from '@/features/products/state/list/context';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { listingTranslate } from '@/i18n/translations/listingTranslations';
import { QueryParamValue } from '@/lib/http/queryParamsBuilder';

const ProductFiltersDrawer = dynamic(
  () =>
    import('@/features/products/components/list/filters/productFiltersDrawer').then(
      (m) => m.ProductFiltersDrawer
    ),
  { ssr: false }
);

export const ProductsFilters = () => {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const currentSearchParams = useSearchParams();
  const pathname = usePathname();

  const selectedFilters = useProductListSelectedFilters();
  const totalItems = useProductListTotalItems();
  const selectedFiltersCount = useProductListSelectedFiltersCount();
  const filters = useProductListFilters();
  const taxonSlug = useProductListTaxonSlug();
  const taxonDefaultSortingOption = useProductListTaxonDefaultSortingOption();

  const defaultOrderBy =
    taxonDefaultSortingOption?.orderBy?.[0] !== undefined
      ? String(taxonDefaultSortingOption.orderBy[0])
      : undefined;
  const defaultSort =
    taxonDefaultSortingOption?.sort?.[0] !== undefined
      ? String(taxonDefaultSortingOption.sort[0])
      : undefined;

  // Remove some query params from the URL.
  const stateFiltersWithoutSelectedParams = useMemo(() => {
    const params = { ...selectedFilters };

    // ? We don't want the query param "taxon" to appear in the browser address bar
    if (params?.taxon && params.taxon.includes(taxonSlug || '')) {
      delete params.taxon;
    }
    if (params?.page) {
      delete params.page;
    }

    return params;
  }, [selectedFilters, taxonSlug]);

  const prevFiltersStrRef = useRef(JSON.stringify(stateFiltersWithoutSelectedParams));
  const justResetPageRef = useRef(false);

  useEffect(() => {
    const currentFiltersStr = JSON.stringify(stateFiltersWithoutSelectedParams);
    const filtersChanged = prevFiltersStrRef.current !== currentFiltersStr;

    const formattedParams = getFormattedFilteredParams(
      stateFiltersWithoutSelectedParams as Record<string, QueryParamValue>,
      defaultOrderBy,
      defaultSort
    );

    const newSearchParams = new URLSearchParams(formattedParams);
    newSearchParams.delete('page');

    if (filtersChanged) {
      justResetPageRef.current = true;
      prevFiltersStrRef.current = currentFiltersStr;
    } else {
      if (justResetPageRef.current) {
        if (!currentSearchParams?.has('page')) {
          justResetPageRef.current = false;
        }
      } else {
        const urlPage = currentSearchParams?.get('page');
        if (urlPage) {
          newSearchParams.set('page', urlPage);
        }
      }
    }

    const queryString = newSearchParams?.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

    const currentUrl = `${pathname}${currentSearchParams?.toString() ? '?' + currentSearchParams?.toString() : ''}`;

    /**
     * TODO: Main functionality is hidden too deep inside this component — move it to a higher-level module.
     * This makes the structure unclear and harder to maintain.
     */
    if (newUrl !== currentUrl) {
      window.history.pushState(null, '', newUrl);
    }
  }, [
    stateFiltersWithoutSelectedParams,
    router,
    pathname,
    defaultOrderBy,
    defaultSort,
    currentSearchParams
  ]);

  const stickyBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filtersStickyBar = stickyBarRef.current;
    if (!filtersStickyBar) return;

    const stuckLine = parseFloat(getComputedStyle(filtersStickyBar).top) + 1;

    const observer = new IntersectionObserver(
      ([entry]) => {
        filtersStickyBar.dataset.filtersStuck = String(
          entry.intersectionRatio > 0 &&
            entry.intersectionRatio < 1 &&
            entry.boundingClientRect.top < stuckLine
        );
      },
      { rootMargin: `-${stuckLine}px 0px 0px 0px`, threshold: 1 }
    );

    observer.observe(filtersStickyBar);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="block lg:hidden mt-1 px-4">
        <p className="mb-1 text-xs text-(--mui-palette-text-disabled)">
          {listingTranslate('product-count')}: {totalItems}
        </p>
        <ProductFiltersDrawer />
      </div>

      <div
        ref={stickyBarRef}
        className="sticky top-17 z-20 flex lg:hidden justify-between gap-4 px-4 pt-2 pb-3 md:-mb-2 bg-(--mui-palette-background-default) data-[filters-stuck=true]:shadow-md"
      >
        <ProductFiltersButton />
        <ProductSortingButton />
      </div>

      <div className="sticky top-34 hidden lg:flex h-fit max-h-[calc(100dvh-11rem)] overflow-y-auto flex-col lg:min-w-67 max-w-67 rounded-3xl px-2 pb-4 bg-(--mui-palette-primary-contrast) shadow-(--mui-shadows-4)">
        {selectedFiltersCount > 0 ? <ResetFiltersButton /> : null}
        {filters.map((filter, index) => (
          <ProductFiltersItem
            key={index + filter.code}
            filter={filter}
            index={index}
            isMobile={!isDesktop}
          />
        ))}
      </div>
    </>
  );
};
